import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { IPricing, Pricing } from "@/app/domain";
import { Delivery, DeliveryItem, DeliveryWeightItem } from "@/app/domain/entities/delivery";
import {
    DeliveryCombinedType,
    DeliveryDirectType,
    minWeightForDirectDelivery,
    SteelSystemCodes
} from "@/app/constants";

import { DeliveryService } from "@/app/services/delivery.service";
import { DeliveryStrategiesRegistry } from "@/app/services/delivery-strategies.service";

import { useSpacesStore } from "@/stores/new/spaces.store";
import { useNewEstimateStore } from "@/stores/new/estimateStore";

export const useDeliveryStore = defineStore(
    "delivery-store",
    () => {
        const estimateStore = useNewEstimateStore();
        const spacesStore = useSpacesStore();

        const hasActiveDelivery = computed(() => activeDelivery.value !== null);
        const spaces = computed(() => spacesStore.spaces);
        const estimate = computed(() => estimateStore.estimate);

        const availableDeliveryTypes = [
            DeliveryCombinedType,
            DeliveryDirectType,
        ];
        const deliveryItems = ref<DeliveryItem[]>([]);
        const deliveryType = ref<string | null>(null);
        const activeDelivery = ref<Delivery | null>(null);

        const totalWeight = ref(0);
        const totalGlassWeight = ref(0);
        const numberOfTracks = ref(0);
        const isIncludeCombinedDeliveryInPrice = ref(true);
        const isChangedCombinedCheckbox = ref(false);
        const totalDeliveryPrice = ref<IPricing>(new Pricing(0, 0, 0))
        const deliverySelectType = ref<string | null>(null);


        const isSteel = computed(() => SteelSystemCodes.includes(estimate.value.installedSystemCode));
        const unit = computed(() => estimate.value.installedSystemCode);

        const createDeliveryItems = () => {
            deliveryItems.value = [];
            availableDeliveryTypes.forEach((type) => {
                const items = DeliveryService.createDeliveryItems(type);
                // @ts-ignore
                deliveryItems.value.push(items);
            })
        };

        const getElementWeight = (type, element) => {
            const registry = new DeliveryStrategiesRegistry();
            const DeliveryStrategy = registry.getStrategy(
                type ?? "",
            );

            if (!DeliveryStrategy) return null;

            const strategy = new DeliveryStrategy(
                element,
                isSteel.value,
                unit.value,
            );

            const partitionElement = strategy.create() as DeliveryWeightItem;
            return {
                sum_weight:
                    +partitionElement.sum.price.toFixed(2),
                glass_weight: +partitionElement.glass_weight.value,
            }
        }


        const getOrderWeight = () => {
            totalWeight.value = 0;
            totalGlassWeight.value = 0;

            spaces.value.forEach((space) => {
                space.getAllPartitions().forEach((partition) => {
                    const partitionWeight = getElementWeight(partition.structureType, partition);
                    totalWeight.value += partitionWeight?.sum_weight || 0;
                    totalGlassWeight.value += partitionWeight?.glass_weight || 0;

                    partition.getAllDoors().forEach((door) => {
                        const doorWeight = getElementWeight(door?.type, door);
                        totalWeight.value += doorWeight?.sum_weight || 0;
                        totalGlassWeight.value += doorWeight?.glass_weight || 0;
                    });

                    partition.getAllOverdoors().forEach((overdoor) => {
                        const overdoorWeight = getElementWeight(overdoor?.type, overdoor);
                        totalWeight.value += overdoorWeight?.sum_weight || 0;
                        totalGlassWeight.value += overdoorWeight?.glass_weight || 0;
                    });
                });
            });
        }

        const defineDeliveryType = (weight: number) => {
            if (estimate.value.deliveryType) {
                deliveryType.value = estimate.value.deliveryType
                return
            }

            if (weight < minWeightForDirectDelivery) {
                deliveryType.value = DeliveryCombinedType;
            } else {
                deliveryType.value = DeliveryDirectType;
            }
        }

        const $restoreDelivery = (deliveryFromApi: Delivery, estimateFromApi) => {
            activeDelivery.value = deliveryFromApi;
            deliveryType.value = estimateFromApi.deliveryType;
            isIncludeCombinedDeliveryInPrice.value = estimateFromApi.isIncludeCombinedDeliveryInPrice;
            isChangedCombinedCheckbox.value = !estimateFromApi.isIncludeCombinedDeliveryInPrice;
            totalGlassWeight.value = estimateFromApi.totalGlassWeight;
        };

        const $reset = () => {
            activeDelivery.value = null;
            totalDeliveryPrice.value = new Pricing(0, 0, 0);
            deliveryItems.value = [];
            deliveryType.value = null;
            totalWeight.value = 0;
            totalGlassWeight.value = 0;
            numberOfTracks.value = 0;
            isIncludeCombinedDeliveryInPrice.value = true;
            isChangedCombinedCheckbox.value = false;
            deliverySelectType.value = null;
        };

        return {
            activeDelivery,
            hasActiveDelivery,
            createDeliveryItems,
            deliveryItems,
            getOrderWeight,
            totalWeight,
            totalGlassWeight,
            defineDeliveryType,
            deliveryType,
            numberOfTracks,
            totalDeliveryPrice,
            isIncludeCombinedDeliveryInPrice,
            isChangedCombinedCheckbox,
            deliverySelectType,
            $reset,
            $restoreDelivery,
        };
    },
    { persist: true },
);
